/**
 * TRIM / EXTEND of an LWPOLYLINE as the TARGET (the object being cut/grown) —
 * MASTERPLAN N3 MODIFY-TRIM-EXTEND-POLY-SPLINE-TARGETS.
 *
 * A polyline is parameterised as a single continuous curve: `param = segment
 * index + local-t` (local-t ∈ [0,1) along that segment — straight lerp for a
 * bulge≈0 segment, `bulgeArcPointAt` for a bulge/arc segment). `param` lives on
 * a LINEAR domain `[0, segCount]` for an OPEN polyline (no cyclic parent curve
 * — mirrors the Line convention) or a CIRCULAR domain of period `segCount` for
 * a CLOSED one (mirrors the full-Arc/Circle/Ellipse convention). Reusing
 * `selectTrimSurvivors` (the SAME parametric-span selector Arc/Circle/Ellipse
 * targets already use) keeps "pick = remove" and the open/closed split logic
 * as ONE truth across every curve family.
 *
 * ## Bulge semantics (Autodesk DXF reference, LWPOLYLINE group 42; `ezdxf`
 * `bulge_to_arc`)
 *
 * `bulge = tan(θ/4)` where θ is the segment's signed included angle. Splitting
 * a bulge segment at local-t ∈ (0,1) yields a SUB-ARC whose own included angle
 * is `θ · Δt` (uniform-angle parameterisation — `bulgeArcPointAt` walks the
 * angle linearly in `u`), so the new bulge is `tan((θ·Δt)/4)`. For an
 * UNTOUCHED segment (Δt = 1 starting at t=0) this formula reduces to the
 * ORIGINAL bulge value exactly (`tan(θ/4) = bulge`); this module still special-
 * cases that (Δt≈[0,1]) to copy the value VERBATIM rather than round-tripping
 * through `atan`/`tan`, guaranteeing byte-exact preservation on unaffected
 * segments (not just numerically-close).
 */
import { bulgeArc, bulgeArcPointAt } from "@dxf-ai-editor/drawing-model";

import {
  bulgeArcToGeom,
  clampCarrierTolerance,
  extendArcTarget,
  polylineCarrierRadialTolerance,
  selectTrimSurvivors,
  sweepDeg,
  type ArcGeom,
  type CarrierTaggedPoint,
  type IntPoint,
} from "./trimExtendGeometry";

const GEO_EPS = 1e-9;

export type PolyVertex = {
  x: number;
  y: number;
  bulge?: number;
  startWidth?: number;
  endWidth?: number;
  vertexId?: number;
};

export type PolylineTargetInput = { vertices: PolyVertex[]; closed: boolean };

/**
 * Where one OUTPUT vertex came from in the SOURCE chain (@codex PR #785 round
 * 4, P2).
 *
 * `PolyVertex` carries only the four LWPOLYLINE group codes (10/20, 40, 41,
 * 42) plus the group-91 id, because that is all a lightweight polyline HAS. A
 * heavy `POLYLINE` vertex is a full DXF record — its own group-5 handle, its
 * `1001` XDATA, its `102 {ACAD_XDICTIONARY` / `{ACAD_REACTORS` blocks, its
 * unmapped groups and its non-canonical `100` subclass extensions — none of
 * which any geometric result type should try to carry. So the geometry says
 * WHERE each output vertex came from and the heavy-polyline adapter
 * (`./heavyPolylineTrimTarget.ts`) copies the record itself: preserve source,
 * never infer, the same rule `vertexId` already follows here. Without it a
 * trimmed heavy polyline came back with every vertex record blanked — the very
 * per-VERTEX loss PR #785 round 1 repaired one level up.
 */
export interface PolylineVertexProvenance {
  /**
   * Index into the SOURCE `vertices` array when this output vertex IS an
   * untouched source vertex (the span boundary landed exactly on it), else
   * `null` for a freshly-cut point that exists in no source record.
   */
  readonly sourceIndex: number | null;
  /**
   * Index of the SOURCE segment this output vertex sits on — the span from
   * `vertices[segmentIndex]` to `vertices[(segmentIndex + 1) % n]`. Always a
   * real segment index (never the unwrapped closed-domain param), so a caller
   * deciding a FRESH point's per-vertex channels has the two records that
   * bracket it.
   */
  readonly segmentIndex: number;
}

export type PolylineTargetResult = {
  vertices: PolyVertex[];
  closed: false;
  /** One entry per `vertices` entry, in the same order. */
  provenance: PolylineVertexProvenance[];
};

// ── Segment-local geometry helpers ──────────────────────────────────

function pointAtSegmentParam(
  v0: PolyVertex,
  v1: PolyVertex,
  bulge: number,
  localT: number,
): IntPoint {
  const arc = bulgeArc(v0, v1, bulge);
  if (!arc) {
    return { x: v0.x + (v1.x - v0.x) * localT, y: v0.y + (v1.y - v0.y) * localT };
  }
  return bulgeArcPointAt(arc, localT);
}

/** Bulge of the sub-arc spanning local range `[lo,hi]` of a segment whose
 * original bulge is `originalBulge`. Exact-preserves the original value when
 * `[lo,hi] ≈ [0,1]` (the whole, untouched segment). */
function subArcBulge(originalBulge: number, lo: number, hi: number): number {
  if (Math.abs(originalBulge) < GEO_EPS) return 0;
  if (lo <= GEO_EPS && hi >= 1 - GEO_EPS) return originalBulge;
  const theta = 4 * Math.atan(originalBulge);
  const subTheta = theta * (hi - lo);
  return Math.tan(subTheta / 4);
}

function closestPointOnBulgeArc(
  arc: NonNullable<ReturnType<typeof bulgeArc>>,
  point: IntPoint,
): { point: IntPoint; localT: number } {
  const dx = point.x - arc.cx;
  const dy = point.y - arc.cy;
  if (Math.hypot(dx, dy) < GEO_EPS) {
    return { point: bulgeArcPointAt(arc, 0), localT: 0 };
  }
  const angle = Math.atan2(dy, dx);
  const twoPi = Math.PI * 2;
  const normalize = (a: number): number => ((a % twoPi) + twoPi) % twoPi;
  const mag = Math.abs(arc.signedSweep);
  // `>= 0` vs `> 0` is a MATHEMATICALLY EQUIVALENT mutant here (and at every
  // other `arc.signedSweep >= 0` check in this module): `arc` is only ever a
  // non-null `bulgeArc(...)` result, and `bulgeArc` itself early-returns
  // `null` for `Math.abs(bulge) < 1e-12` — so any `arc` reaching this line has
  // `theta = 4·atan(bulge)` with `|bulge| >= 1e-12`, hence `|theta| > 0`
  // (atan is 0 only at 0), hence `signedSweep = ∓|theta|` is PROVABLY
  // non-zero. `signedSweep` can therefore never equal exactly `0` at any of
  // these call sites, making `>= 0` and `> 0` observationally identical for
  // every reachable input (verified: `nurbsKnotOps`-style property coverage
  // would need to violate `bulgeArc`'s own non-null invariant to disagree).
  //
  // NOTE: the `Stryker disable next-line` directive must sit DIRECTLY
  // above the `arc.signedSweep >= 0` line itself, not above `const offset
  // =` — Stryker's `DirectiveBookkeeper` resolves "next-line" against the
  // MUTATED expression's own `loc.start.line`, not the enclosing multi-
  // line statement's first line; a comment attached to the `const`
  // keyword's line does not match and silently fails to suppress.
  const offset =
    // Stryker disable next-line EqualityOperator: signedSweep is provably non-zero for any non-null arc reaching this line (see equivalent-mutant note above)
    arc.signedSweep >= 0
      ? normalize(angle - arc.startAngle)
      : normalize(arc.startAngle - angle);
  if (mag > GEO_EPS && offset <= mag + 1e-9) {
    return { point: bulgeArcPointAt(arc, offset / mag), localT: offset / mag };
  }
  // Angle falls outside the swept range — closest point is whichever endpoint
  // is nearer (mirrors how a line-segment projection clamps to [0,1]).
  const p0 = bulgeArcPointAt(arc, 0);
  const p1 = bulgeArcPointAt(arc, 1);
  const d0 = Math.hypot(point.x - p0.x, point.y - p0.y);
  const d1 = Math.hypot(point.x - p1.x, point.y - p1.y);
  return d0 <= d1 ? { point: p0, localT: 0 } : { point: p1, localT: 1 };
}

/** Shared core of `nearestPolylineParam` — a GLOBAL nearest-segment search,
 * optionally skipping segment indices in `excludeSegments` (used by
 * `resolveCutParams` below to disambiguate a self-intersection's duplicate
 * coordinate). */
function nearestPolylineParamCore(
  vertices: PolyVertex[],
  closed: boolean,
  point: IntPoint,
  excludeSegments: ReadonlySet<number> | null,
): { param: number; point: IntPoint; distance: number } {
  const n = vertices.length;
  const segCount = closed ? n : n - 1;
  let best = { param: 0, point: { x: vertices[0]!.x, y: vertices[0]!.y }, distance: Infinity };
  for (let i = 0; i < segCount; i++) {
    if (excludeSegments?.has(i)) continue;
    const v0 = vertices[i]!;
    const v1 = vertices[(i + 1) % n]!;
    const bulge = v0.bulge ?? 0;
    const arc = bulgeArc(v0, v1, bulge);
    const candidate = arc
      ? closestPointOnBulgeArc(arc, point)
      : (() => {
          const dx = v1.x - v0.x;
          const dy = v1.y - v0.y;
          const len2 = dx * dx + dy * dy;
          // `len2` is a SQUARED length — comparing it against the linear-
          // scale `GEO_EPS` (Codex P2, round 8) misclassifies any segment
          // shorter than `sqrt(GEO_EPS) ≈ 3.16e-5` as degenerate, not just
          // genuinely near-zero-length ones. Compare against `GEO_EPS ** 2`
          // so the effective length threshold matches `GEO_EPS` itself.
          const t =
            len2 < GEO_EPS * GEO_EPS
              ? 0
              : Math.min(1, Math.max(0, ((point.x - v0.x) * dx + (point.y - v0.y) * dy) / len2));
          return { point: { x: v0.x + dx * t, y: v0.y + dy * t }, localT: t };
        })();
    const d = Math.hypot(point.x - candidate.point.x, point.y - candidate.point.y);
    if (d < best.distance) {
      best = { param: i + candidate.localT, point: candidate.point, distance: d };
    }
  }
  return best;
}

/** Nearest polyline parameter (segment-index + local-t) to a world point,
 * projecting onto each segment's EXACT carrier (line or bulge circle) — the
 * same "snap onto the real curve" refinement this module applies everywhere a
 * caller hands in an (approximate, tessellation-derived) intersection point. */
export function nearestPolylineParam(
  vertices: PolyVertex[],
  closed: boolean,
  point: IntPoint,
): { param: number; point: IntPoint; distance: number } {
  return nearestPolylineParamCore(vertices, closed, point, null);
}

/** Two input coordinates within this distance are treated as "the same
 * intersection point" for `resolveCutParams`'s self-intersection
 * disambiguation — a genuine duplicate hit (two segments crossing the SAME
 * cutting edge at the SAME physical point) comes out of `polyPolyIntersection`
 * numerically exact (same underlying line-line solve), so a tight tolerance
 * safely distinguishes it from two merely-nearby-but-distinct cuts. */
const COINCIDENT_CUT_TOL = 1e-6;

/**
 * Resolve MULTIPLE cutting-edge intersection points to polyline parameters,
 * one call per point — EXCEPT when a point's coordinate coincides with an
 * EARLIER point's in this same batch, in which case the segment(s) already
 * claimed by that earlier point are excluded from the search.
 *
 * `entityIntersectionPoints`'s tessellation fallback (`polyPolyIntersection`)
 * carries no per-hit segment/parameter provenance — a self-intersecting
 * OPEN polyline whose two crossing branches are both cut by the SAME
 * boundary edge at their shared point reports that point TWICE, as two
 * numerically-identical coordinates. Resolving each occurrence independently
 * via the plain global-nearest search (`nearestPolylineParam`) always ties to
 * the SAME (first-found) segment — collapsing two genuinely DISTINCT cut
 * parameters into one and corrupting `selectTrimSurvivors`'s span selection
 * (Codex P2). Excluding already-claimed segments for a repeated coordinate
 * resolves the SECOND occurrence to its own (still exact-distance-tied)
 * segment instead.
 *
 * Handles the common case — exactly two segments crossing at one point.
 * Three or more segments meeting at the EXACT same point is not handled
 * (would need a 3rd exclusion round); a fully general fix needs
 * `entityIntersectionPoints` itself to carry segment/parameter provenance
 * through `polyPolyIntersection` — a broader change spanning every curve
 * target family (`trimSplineTarget`'s point inversion has the identical
 * class of gap, per the same Codex review), not scoped to this module alone.
 * Documented follow-up, MASTERPLAN N3 body.
 *
 * A repeated coordinate does NOT always mean "a self-intersection's second
 * branch", though (Codex P2, round 4): two DIFFERENT cutting edges (or a
 * tessellation artefact) can just as easily cross the SAME single segment at
 * the SAME point — the target need not self-intersect at all. Excluding the
 * already-claimed segment unconditionally would then force the repeat onto
 * whatever OTHER segment happens to be nearest overall — often a distant,
 * unrelated one — inventing a bogus cut. Guard: only accept the excluded
 * search's result when it is ALSO an exact (tolerance-level) coordinate
 * match — i.e. genuinely ANOTHER segment passes through this exact point
 * too. Otherwise fall back to the plain (unexcluded) resolution, which
 * simply re-confirms the SAME segment — a harmless duplicate `cutParams`
 * entry, not a fabricated one.
 */
function resolveCutParams(
  vertices: PolyVertex[],
  closed: boolean,
  points: IntPoint[],
): number[] {
  const seen: Array<{ point: IntPoint; segIdx: number }> = [];
  return points.map((p) => {
    const excluded = new Set<number>();
    for (const s of seen) {
      if (Math.hypot(s.point.x - p.x, s.point.y - p.y) <= COINCIDENT_CUT_TOL) {
        excluded.add(s.segIdx);
      }
    }
    let resolved = nearestPolylineParamCore(vertices, closed, p, excluded.size > 0 ? excluded : null);
    if (excluded.size > 0 && resolved.distance > COINCIDENT_CUT_TOL) {
      // No OTHER segment genuinely passes through this exact point — the
      // repeat belongs to the SAME (excluded) segment as the earlier point,
      // not a distinct self-intersection branch. Re-resolve without
      // exclusion instead of accepting a bogus far-away match.
      resolved = nearestPolylineParamCore(vertices, closed, p, null);
    }
    const n = vertices.length;
    const segCount = closed ? n : n - 1;
    const segIdx = Math.min(Math.floor(resolved.param), segCount - 1);
    seen.push({ point: p, segIdx });
    return resolved.param;
  });
}

/** Integers strictly between `start` and `end`, walking FORWARD on an
 * UNWRAPPED continuous axis (caller has already added `period` to `end` when
 * a closed-domain survivor crosses the seam — see `buildPolylineVertexRange`).
 * Deliberately does NOT wrap the returned breakpoints modulo `period`: a
 * wrapped breakpoint (e.g. the seam vertex stored as `0` instead of `period`)
 * collapses two distinct axis positions — "end of the last segment" and
 * "start of the first segment" — into one value, corrupting the FOLLOWING
 * segment's `localHi` (Codex P1 finding). Modulo is applied by the caller
 * only at the point of vertex-array indexing, never to a stored param. */
function integerBreakpointsForward(start: number, end: number): number[] {
  const out: number[] = [];
  for (let k = Math.floor(start) + 1; k < end - GEO_EPS; k++) {
    out.push(k);
  }
  return out;
}

/** Rebuild the vertex list for the surviving span `[lo,hi]` (params in
 * segment-index units), preserving bulge/width/vertexId on every UNTOUCHED
 * original vertex it passes through and computing exact sub-arc bulge at
 * either freshly-cut end. Always returns an OPEN chain (a trimmed CLOSED
 * polyline becomes an open one — AutoCAD parity, matching Circle→Arc). */
function buildPolylineVertexRange(
  vertices: PolyVertex[],
  closed: boolean,
  lo: number,
  hi: number,
): { vertices: PolyVertex[]; provenance: PolylineVertexProvenance[] } {
  const n = vertices.length;
  const segCount = closed ? n : n - 1;
  // A CLOSED survivor crossing the 0/segCount seam has `hi < lo` (both are
  // wrapped into `[0,segCount)` by `selectTrimSurvivors`). Unwrap `hi` onto
  // the SAME continuous axis as `lo` for every range computation below;
  // modulo is applied only when an actual `vertices[]` index is needed.
  const effectiveHi = closed && hi < lo - GEO_EPS ? hi + segCount : hi;
  const mids = closed
    ? integerBreakpointsForward(lo, effectiveHi)
    : (() => {
        const out: number[] = [];
        for (let k = Math.floor(lo) + 1; k < effectiveHi - GEO_EPS; k++) out.push(k);
        return out;
      })();
  const params = [lo, ...mids, effectiveHi];

  const result: PolyVertex[] = [];
  const provenance: PolylineVertexProvenance[] = [];
  for (let idx = 0; idx < params.length; idx++) {
    const param = params[idx]!;
    // OPEN: clamp the raw floor so `param === segCount` (the untouched final
    // endpoint of the whole chain) resolves to the LAST segment with
    // `localAt === 1`, not an out-of-bounds `segIdxRaw === segCount`.
    // CLOSED: no clamp — `segIdxRaw` stays on the unwrapped continuous axis
    // (can exceed `segCount`) and only `segIdx` (the array-index projection)
    // is wrapped modulo `segCount`, so `localAt` is always this segment's own
    // fractional offset, never a cross-seam artifact.
    const segIdxRaw = closed ? Math.floor(param) : Math.min(Math.floor(param), segCount - 1);
    const segIdx = closed ? ((segIdxRaw % segCount) + segCount) % segCount : segIdxRaw;
    const v0 = vertices[segIdx]!;
    const v1 = vertices[(segIdx + 1) % n]!;
    const localAt = param - segIdxRaw;
    const pt = pointAtSegmentParam(v0, v1, v0.bulge ?? 0, localAt);
    const startW = v0.startWidth ?? 0;
    const endW = v0.endWidth ?? 0;
    // The retained point can coincide with EITHER v0 (localAt≈0 — the common
    // case) OR v1 (localAt≈1 — only reachable on an OPEN chain's untouched
    // final endpoint, where `segIdx` is clamped one short of `param`, see
    // above; Codex P2 finding). Copy the ORIGINAL vertex's own metadata in
    // either case so a group-91 `vertexId` constraint reference never dangles.
    const isAtV0 = Math.abs(localAt) <= GEO_EPS;
    const isAtV1 = !isAtV0 && Math.abs(localAt - 1) <= GEO_EPS;

    let bulge = 0;
    let newEndWidth = 0;
    if (idx < params.length - 1) {
      const localHi = params[idx + 1]! - segIdxRaw;
      bulge = subArcBulge(v0.bulge ?? 0, localAt, localHi);
      newEndWidth = startW + (endW - startW) * localHi;
    }
    const newStartWidth = startW + (endW - startW) * localAt;

    // The `isAtV1` case (Codex P2, round 5) is the untouched FINAL vertex of
    // an open chain — there is no OUTGOING segment from it to interpolate
    // width/bulge across (the `idx < params.length - 1` branch above never
    // runs for it, and `v0`-based `newStartWidth` reinterprets `v0`'s OWN
    // endWidth as if it belonged to `v1`). Copy `v1`'s own group 40/41/42
    // fields VERBATIM instead — preserve source, never infer (this repo's
    // round-trip convention) — exactly like `vertexId` just above.
    const bulgeOut = isAtV1 ? (v1.bulge ?? 0) : bulge;
    const startWidthOut = isAtV1 ? (v1.startWidth ?? 0) : newStartWidth;
    const endWidthOut = isAtV1 ? (v1.endWidth ?? 0) : newEndWidth;

    result.push({
      x: pt.x,
      y: pt.y,
      bulge: bulgeOut,
      startWidth: startWidthOut,
      endWidth: endWidthOut,
      ...(isAtV0 && v0.vertexId !== undefined
        ? { vertexId: v0.vertexId }
        : isAtV1 && v1.vertexId !== undefined
          ? { vertexId: v1.vertexId }
          : {}),
    });
    // Same `isAtV0` / `isAtV1` decision the `vertexId` copy above makes, and
    // deliberately made HERE rather than re-derived by the caller from the
    // params: `segIdxRaw`'s open-chain clamp and closed-chain unwrap are this
    // function's own rule, and a second copy of it would be a second truth.
    provenance.push({
      sourceIndex: isAtV0 ? segIdx : isAtV1 ? (segIdx + 1) % n : null,
      segmentIndex: segIdx,
    });
  }
  return { vertices: result, provenance };
}

/**
 * `entityIntersectionPoints(polylineTarget, edge, {targetInfinite:true})`
 * intersects a boundary/cutting edge against the polyline's COMBINED
 * infinite EXTEND carrier — `polylineInfiniteCarrier`'s `[prefix(start
 * carrier), ...real, suffix(end carrier)]` — in ONE tessellated pass, so a
 * multi-segment polyline's `boundaryPoints` can contain crossings that
 * belong to the OTHER (non-growing) end's carrier, not the end actually
 * being extended (Codex P1). Blindly projecting/angle-converting every point
 * in that union onto the CHOSEN end's own line/circle — as if it were
 * necessarily a genuine crossing of THAT carrier — can extend to a point the
 * boundary edge never actually touches. Filter to carrier MEMBERSHIP first.
 */

/** Perpendicular-distance-from-line filter for a STRAIGHT terminal segment's
 * own (stretched) infinite carrier. Tolerance is relative to the segment's
 * own length scale, CLAMPED into `[CARRIER_TOLERANCE_FLOOR,
 * CARRIER_TOLERANCE_CEILING]` (Codex P2, round 7 — a bare `len * 1e-6` grows
 * unboundedly for a very long segment, wide enough to wrongly accept a
 * crossing that actually belongs to the OTHER end's carrier) — a genuine
 * crossing of the exact stretched line is numerically exact up to ordinary
 * floating-point roundoff; a crossing that belongs to the OTHER end's
 * carrier is, in general position, nowhere near this line at all. */
function filterToLineCarrier(
  points: IntPoint[],
  v0: IntPoint,
  ux: number,
  uy: number,
  len: number,
): IntPoint[] {
  const tol = clampCarrierTolerance(len * 1e-6);
  return points.filter((bp) => Math.abs((bp.x - v0.x) * uy - (bp.y - v0.y) * ux) <= tol);
}

/**
 * Filter `points` to those genuinely on the circle `(cx,cy,radius)`. When a
 * candidate carries `carrier` provenance (Codex P2, round 15 —
 * `entityIntersectionPoints` tags hits by which terminal's own tessellation
 * region produced them), an EXACT provenance match against `expectedCarrier`
 * is used instead of the geometric tolerance — provenance is definitionally
 * certain (the point was generated FROM this exact circle's own
 * tessellation), whereas NO fixed radial-distance tolerance can fully
 * distinguish "ordinary tessellation error on THIS circle" from "a genuine
 * point on a DIFFERENT, geometrically nearby circle" (rounds 12/14/15).
 * Untagged candidates (no provenance available — e.g. from a Spline/Ellipse
 * boundary edge's own tessellation) fall back to the tolerance, unchanged.
 */
function filterToCircleCarrier(
  points: CarrierTaggedPoint[],
  cx: number,
  cy: number,
  radius: number,
  expectedCarrier?: "start" | "end",
): IntPoint[] {
  const tol = polylineCarrierRadialTolerance(radius);
  return points.filter((bp) => {
    if (expectedCarrier && bp.carrier) return bp.carrier === expectedCarrier;
    return Math.abs(Math.hypot(bp.x - cx, bp.y - cy) - radius) <= tol;
  });
}

// ── Public TRIM / EXTEND entry points ───────────────────────────────

/**
 * Trim an LWPOLYLINE target: shorten/split it at the cutting-edge
 * intersections nearest the pick, exactly like `trimArcTarget` but at
 * SEGMENT granularity. Returns one survivor (near-end trim) or two
 * (mid-polyline split into two open chains), or `null` for a no-op.
 */
export function trimPolylineTarget(
  polyline: PolylineTargetInput,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): PolylineTargetResult[] | null {
  const { vertices, closed } = polyline;
  if (vertices.length < 2) return null;
  const segCount = closed ? vertices.length : vertices.length - 1;
  if (segCount < 1) return null;

  const cutParams = resolveCutParams(vertices, closed, cutPoints);
  const pickParam = nearestPolylineParam(vertices, closed, pickPoint).param;
  const openDomain = closed ? null : { t0: 0, t1: segCount };
  const survivors = selectTrimSurvivors(cutParams, pickParam, segCount, openDomain);
  if (survivors.length === 0) return null;

  const results: PolylineTargetResult[] = [];
  for (const [lo, hi] of survivors) {
    const { vertices: vs, provenance } = buildPolylineVertexRange(vertices, closed, lo, hi);
    // EQUIVALENT MUTANT (ConditionalExpression "true" stub): this guard's
    // own condition is UNCONDITIONALLY true by construction —
    // `buildPolylineVertexRange`'s own `params` array is always
    // `[lo, ...mids, effectiveHi]` (structurally >=2 entries regardless
    // of `lo`/`hi`), and its result array is built with exactly one push
    // per `params` entry, so `vs.length === params.length >= 2` always.
    // No input can make `vs.length < 2`, so a "true" stub is behaviourally
    // identical to the real check for every reachable input.
    // Stryker disable next-line ConditionalExpression: vs.length is always >= 2 by construction of buildPolylineVertexRange's own params array (see equivalent-mutant note above)
    if (vs.length >= 2) results.push({ vertices: vs, closed: false, provenance });
  }
  // EQUIVALENT MUTANT (ConditionalExpression "true" stub, EqualityOperator
  // `>`->`>=`): by this point `survivors.length >= 1` is already
  // guaranteed (the `survivors.length === 0` guard above already
  // returned). The loop above pushes exactly one `results` entry per
  // survivor (the `vs.length >= 2` filter — itself proven equivalent-
  // always-true just above — never actually drops one), so
  // `results.length === survivors.length >= 1` is ALSO a mathematical
  // certainty by the time this line runs. `results.length > 0` can
  // therefore never observe `results.length === 0`, making both a "true"
  // stub and a `>= 0` mutant (also always true, since `.length` is never
  // negative) behaviourally identical to the real check.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: results.length is always >= 1 by the time this line runs (see equivalent-mutant note above)
  return results.length > 0 ? results : null;
}

/**
 * Extend an LWPOLYLINE target: grow the end nearest the pick out to the
 * closest boundary crossing beyond it, by MOVING the terminal vertex (never
 * inserting a new one) — a straight terminal segment simply gets longer; a
 * bulge terminal segment grows its sweep along the SAME centre/radius circle
 * (delegates to the already-proven `extendArcTarget` for that sub-problem, the
 * SAME "outside gap, nearest boundary" logic a standalone Arc entity uses).
 * A CLOSED polyline has no dangling end and cannot be extended (`null`).
 */
export function extendPolylineTarget(
  polyline: PolylineTargetInput,
  boundaryPoints: CarrierTaggedPoint[],
  pickPoint: IntPoint,
): PolylineTargetResult | null {
  const { vertices, closed } = polyline;
  if (closed || vertices.length < 2 || boundaryPoints.length === 0) return null;
  const n = vertices.length;
  const segCount = n - 1;
  // EXTEND never inserts or drops a vertex — it MOVES the terminal one — so
  // every output vertex is its own source record and the provenance is the
  // identity map. `segmentIndex` uses the same "clamp the final endpoint onto
  // the LAST segment" convention `buildPolylineVertexRange` uses, so a caller
  // reading provenance does not have to know which entry point produced it.
  const identityProvenance = (): PolylineVertexProvenance[] =>
    vertices.map((_, i) => ({
      sourceIndex: i,
      segmentIndex: Math.min(i, segCount - 1),
    }));

  const startPt: IntPoint = { x: vertices[0]!.x, y: vertices[0]!.y };
  const endPt: IntPoint = { x: vertices[n - 1]!.x, y: vertices[n - 1]!.y };
  const distStart = Math.hypot(pickPoint.x - startPt.x, pickPoint.y - startPt.y);
  const distEnd = Math.hypot(pickPoint.x - endPt.x, pickPoint.y - endPt.y);
  const extendEndSide = distEnd <= distStart;

  const segIdx = extendEndSide ? segCount - 1 : 0;
  const v0 = vertices[segIdx]!;
  const v1 = vertices[(segIdx + 1) % n]!;
  const bulge = v0.bulge ?? 0;
  const arc = bulgeArc(v0, v1, bulge);

  if (!arc) {
    // Straight terminal segment — grow along its own infinite line, exactly
    // like the Line-target EXTEND path.
    const dx = v1.x - v0.x;
    const dy = v1.y - v0.y;
    const len = Math.hypot(dx, dy);
    if (len < GEO_EPS) return null;
    const ux = dx / len;
    const uy = dy / len;
    const candidates = filterToLineCarrier(boundaryPoints, v0, ux, uy, len);
    let best: IntPoint | null = null;
    let bestDelta = Infinity;
    for (const bp of candidates) {
      const t = (bp.x - v0.x) * ux + (bp.y - v0.y) * uy;
      const delta = extendEndSide ? t - len : -t;
      if (delta < GEO_EPS) continue;
      // EQUIVALENT MUTANT (EqualityOperator `<` -> `<=`): `best` is
      // computed from `t` alone (`v0 + (ux,uy)*t`), never from the
      // winning candidate's OWN coordinates — and `delta` is an
      // invertible linear function of `t` (`t - len` or `-t`), so two
      // candidates with an IDENTICAL `delta` necessarily have an
      // IDENTICAL `t`, and therefore an IDENTICAL `best` regardless of
      // which one "wins" a `<` vs `<=` tie. No test can observe which
      // specific candidate index broke a tie through this output.
      // Stryker disable next-line EqualityOperator: best is derived purely from t, and a delta tie implies an identical t regardless of tie-break direction (see equivalent-mutant note above)
      if (delta < bestDelta) {
        bestDelta = delta;
        best = { x: v0.x + ux * t, y: v0.y + uy * t };
      }
    }
    if (!best) return null;
    const newVertices = [...vertices];
    if (extendEndSide) newVertices[n - 1] = { ...vertices[n - 1]!, x: best.x, y: best.y };
    else newVertices[0] = { ...vertices[0]!, x: best.x, y: best.y };
    return { vertices: newVertices, closed: false, provenance: identityProvenance() };
  }

  // Bulge terminal segment — delegate the "grow along this circle" math to
  // `extendArcTarget`. `ArcGeom` is always CCW start→end (DXF ARC convention);
  // a NEGATIVE `signedSweep` (the polyline's CW-from-v0 bulge case) means v1 is
  // actually the geometric START of the CCW-equivalent arc — swap accordingly.
  // `bulgeArc`'s `startAngle` comes from `Math.atan2` and can be ANY value in
  // (-180°,180°] — normalise both angles into [0,360) before handing them to
  // `extendArcTarget`. Passing a raw negative `startAngle` through unnormalised
  // produced a real bug: `extendArcTarget` returns its (unmodified) startAngle
  // alongside a `normDeg`-wrapped endAngle, so `sweepDeg`'s RAW
  // `endDeg − startDeg` (e.g. `286° − (−101°) = 387°`) could spuriously trip
  // the "over-full arc, treat as a full circle" guard on an ordinary small
  // extension, producing a garbage `Math.tan` blow-up for the new bulge.
  // `bulgeArcToGeom` (trimExtendGeometry.ts) is the shared one-truth for this
  // swap/normalise conversion — round 11 extracted it so the analytic
  // tangent-detection supplement (`bulgeSegmentAnalyticHits`) doesn't grow a
  // second independently-hand-written copy.
  const arcGeom: ArcGeom = bulgeArcToGeom(arc);
  // Passing the EXACT vertex being grown as the pick lets `extendArcTarget`'s
  // own nearest-endpoint selection resolve to the correct side regardless of
  // the CCW/CW swap above.
  const growingVertex = extendEndSide ? v1 : v0;
  const carrierBoundaryPoints = filterToCircleCarrier(
    boundaryPoints,
    arc.cx,
    arc.cy,
    arc.radius,
    extendEndSide ? "end" : "start",
  );
  const grown = extendArcTarget(arcGeom, carrierBoundaryPoints, growingVertex);
  if (!grown) return null;

  const totalSweepDeg = sweepDeg(grown.startAngle, grown.endAngle);
  const totalSweepRad = (totalSweepDeg * Math.PI) / 180;
  // EQUIVALENT MUTANT (ArithmeticOperator `*` -> `/`): `originalTheta` is
  // used ONLY to compute `sign` below (never its own magnitude), and
  // `sign(4 * atan(bulge)) === sign(4 / atan(bulge))` for EVERY possible
  // nonzero `atan(bulge)` — multiplying or dividing a nonzero number by a
  // positive constant (4) always preserves its sign. `atan(bulge)` is
  // itself provably nonzero here (the `bulgeArc` non-null invariant — see
  // the `signedSweep`/`>= 0` equivalent-mutant note just below). Verified
  // numerically across representative bulge magnitudes (1e-6 to 100, both
  // signs). No test can observe this mutant through `sign` or anything
  // downstream of it.
  // Stryker disable next-line ArithmeticOperator: originalTheta's magnitude is never used, only its sign, which * and / both preserve identically for a nonzero atan(bulge) (see equivalent-mutant note above)
  const originalTheta = 4 * Math.atan(bulge);
  // `>= 0` vs `> 0`: also equivalent — `bulge` here has already survived
  // `bulgeArc`'s `|bulge| < 1e-12` null guard (this branch is only reached
  // when `arc` is non-null), so `originalTheta = 4·atan(bulge)` is provably
  // non-zero (same reasoning as `closestPointOnBulgeArc`'s comment above).
  // Stryker disable next-line EqualityOperator: originalTheta is provably non-zero for a non-null arc's bulge (see equivalent-mutant note above)
  const sign = originalTheta >= 0 ? 1 : -1;
  const newBulge = Math.tan((sign * totalSweepRad) / 4);

  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const pointAtDeg = (deg: number): IntPoint => ({
    x: arc.cx + arc.radius * Math.cos(toRad(deg)),
    y: arc.cy + arc.radius * Math.sin(toRad(deg)),
  });
  // Which returned angle corresponds to the vertex that actually moved.
  // `>= 0` vs `> 0`: mathematically equivalent — see
  // `closestPointOnBulgeArc`'s comment above (and its note on WHY the
  // disable directive must sit directly above the `arc.signedSweep`
  // expression itself, not above the enclosing `const` statement).
  const movedAngleDeg =
    // Stryker disable next-line EqualityOperator: signedSweep is provably non-zero for any non-null arc reaching this line (see equivalent-mutant note above)
    arc.signedSweep >= 0
      ? extendEndSide
        ? grown.endAngle
        : grown.startAngle
      : extendEndSide
        ? grown.startAngle
        : grown.endAngle;
  const movedPoint = pointAtDeg(movedAngleDeg);

  const newVertices = [...vertices];
  if (extendEndSide) {
    newVertices[segIdx] = { ...v0, bulge: newBulge };
    newVertices[n - 1] = { ...vertices[n - 1]!, x: movedPoint.x, y: movedPoint.y };
  } else {
    newVertices[0] = { ...vertices[0]!, x: movedPoint.x, y: movedPoint.y, bulge: newBulge };
  }
  return { vertices: newVertices, closed: false, provenance: identityProvenance() };
}
